import { PlusCircleIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useMemoryEntries } from '../../hooks/useMemoryEntries';
import { i18nService } from '../../services/i18n';
import type { CoworkUserMemoryEntry } from '../../types/cowork';

/** 普通事实管理独立于设置提交；设置与对话入口可以复用。 */
export default function MemoryEntriesPanel() {
  const { entries, stats, query, setQuery, draft, setDraft, editingId, editorOpen, loading, saving, error, hasMore, reload, more, save, remove, openEditor, closeEditor } = useMemoryEntries();
  /** 删除前展示完整目标文本，避免图标误触直接改变记忆。 */
  const removeEntry = (entry: CoworkUserMemoryEntry) => {
    if (window.confirm(`${i18nService.t('confirmDelete')}：${entry.text}`)) void remove(entry.id);
  };
  const getMemoryStatusLabel = (status: CoworkUserMemoryEntry['status']) => i18nService.t(status === 'created' ? 'coworkMemoryStatusActive' : status === 'stale' ? 'coworkMemoryStatusInactive' : 'coworkMemoryStatusDeleted');
  const formatMemoryUpdatedAt = (timestamp: number) => Number.isFinite(timestamp) && timestamp > 0 ? new Date(timestamp).toLocaleString() : '-';
  return (
  <div className="space-y-3 rounded-xl border px-4 py-4 dark:border-claude-darkBorder border-claude-border">
    <div className="flex items-center justify-between">
      <div className="space-y-1">
        <div className="text-sm font-medium dark:text-claude-darkText text-claude-text">
          {i18nService.t('coworkMemoryCrudTitle')}
        </div>
        {stats && (
          <div className="flex items-center gap-3 text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary">
            <span>{i18nService.t('coworkMemoryTotalLabel')} {stats.created + stats.stale}</span>
            <span className="text-green-500">{i18nService.t('coworkMemoryActiveLabel')} {stats.created}</span>
            <span>{i18nService.t('coworkMemoryInactiveLabel')} {stats.stale}</span>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => openEditor()}
        className="inline-flex items-center justify-center px-3 py-1.5 rounded-lg bg-claude-accent hover:bg-claude-accentHover text-white text-sm transition-colors active:scale-[0.98]"
      >
        <PlusCircleIcon className="h-4 w-4 mr-1.5" />
        {i18nService.t('coworkMemoryCrudCreate')}
      </button>
    </div>

    <input
      type="text"
      disabled={saving} aria-label={i18nService.t('coworkMemorySearchPlaceholder')} value={query}
      onChange={(event) => setQuery(event.target.value)}
      placeholder={i18nService.t('coworkMemorySearchPlaceholder')}
      className="w-full rounded-lg border px-3 py-2 text-sm dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-claude-surface"
    />

    {error && <p role="alert" className="text-xs text-red-600 dark:text-red-400">{error}</p>}
    <button type="button" disabled={loading || saving} onClick={() => void reload()} className="text-xs text-claude-accent">{i18nService.t('refresh')}</button>
    {editorOpen && (
      <div className="rounded-xl border dark:border-claude-accent/40 border-claude-accent/40 dark:bg-claude-darkSurface bg-claude-surface p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium dark:text-claude-darkText text-claude-text">
            {editingId ? i18nService.t('coworkMemoryCrudUpdate') : i18nService.t('coworkMemoryCrudCreate')}
          </h4>
          {editingId && (
            <span className="rounded-full border px-2 py-0.5 text-[10px] dark:border-claude-darkBorder border-claude-border dark:text-claude-darkTextSecondary text-claude-textSecondary">
              {i18nService.t('coworkMemoryEditingTag')}
            </span>
          )}
        </div>
        <textarea
          disabled={saving} maxLength={360} aria-label={i18nService.t('coworkMemoryCrudTextPlaceholder')} value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={i18nService.t('coworkMemoryCrudTextPlaceholder')}
          autoFocus
          className="min-h-[120px] w-full rounded-lg border px-3 py-2 text-sm dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-claude-surface dark:text-claude-darkText text-claude-text focus:border-claude-accent focus:ring-1 focus:ring-claude-accent/30"
        />
        <div className="flex justify-end space-x-2">
          <button
            type="button"
            disabled={saving} onClick={closeEditor}
            className="px-3 py-1.5 text-sm dark:text-claude-darkText text-claude-text dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover rounded-lg border dark:border-claude-darkBorder border-claude-border transition-colors"
          >
            {i18nService.t('cancel')}
          </button>
          <button
            type="button"
            onClick={() => { void save(); }}
            disabled={!draft.trim() || saving}
            className="px-3 py-1.5 text-sm text-white bg-claude-accent hover:bg-claude-accentHover rounded-lg disabled:opacity-60 disabled:cursor-not-allowed transition-colors active:scale-[0.98]"
          >
            {editingId ? i18nService.t('save') : i18nService.t('coworkMemoryCrudCreate')}
          </button>
        </div>
      </div>
    )}

    <div className="max-h-[500px] overflow-auto rounded-lg border dark:border-claude-darkBorder border-claude-border">
      {loading ? (
        <div className="px-3 py-3 text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary">
          {i18nService.t('loading')}
        </div>
      ) : !error && entries.length === 0 ? (
        <div className="px-3 py-3 text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary">
          {i18nService.t('coworkMemoryEmpty')}
        </div>
      ) : (
        <div className="divide-y dark:divide-claude-darkBorder divide-claude-border">
          {entries.map((entry) => (
            <div key={entry.id} className="px-3 py-2.5 text-xs hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors group">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium dark:text-claude-darkText text-claude-text break-words">
                    {entry.text}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-1 dark:text-claude-darkTextSecondary text-claude-textSecondary">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                      entry.status === 'created'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                    }`}>
                      {getMemoryStatusLabel(entry.status)}
                    </span>
                    <span className="text-[10px]">
                      {formatMemoryUpdatedAt(entry.updatedAt)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    type="button"
                    disabled={saving} onClick={() => openEditor(entry)}
                    className="rounded-lg p-1.5 dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover hover:text-claude-accent transition-colors"
                    title={i18nService.t('edit')}
                  >
                    <PencilIcon className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => { void removeEntry(entry); }}
                    className="rounded-lg p-1.5 dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-500 disabled:opacity-60 transition-colors"
                    disabled={loading || saving}
                    title={i18nService.t('delete')}
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    {hasMore && <button type="button" disabled={loading || saving} onClick={() => void more()} className="text-xs text-claude-accent">{i18nService.t('memoryCatalogMore')}</button>}
  </div>
  );
}
